import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import AccidentHistoryModal from '../components/accident_histories/AccidentHistoryModal';
import EmploymentHistoryModal from '../components/employment_histories/EmploymentHistoryModal';
import DriverExperienceModal from '../components/driver_experiences/DriverExperienceModal';
import GuarantorModal from '../components/guarantors/GuarantorModal';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';

const PotentialDriverDetail = () => {
    const { isDark } = useTheme();
    // Get ID from URL path
    const id = window.location.pathname.split('/').pop();
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAccidentModal, setShowAccidentModal] = useState(false);
    const [accidentHistories, setAccidentHistories] = useState([]);
    const [editingAccident, setEditingAccident] = useState(null);
    const [showEmploymentModal, setShowEmploymentModal] = useState(false);
    const [employmentHistories, setEmploymentHistories] = useState([]);
    const [editingEmployment, setEditingEmployment] = useState(null);
    const [showExperienceModal, setShowExperienceModal] = useState(false);
    const [driverExperiences, setDriverExperiences] = useState([]);
    const [editingExperience, setEditingExperience] = useState(null);
    const [showGuarantorModal, setShowGuarantorModal] = useState(false);
    const [guarantors, setGuarantors] = useState([]);
    const [editingGuarantor, setEditingGuarantor] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    useEffect(() => {
        fetchDriverDetails();
        fetchAccidentHistories();
        fetchEmploymentHistories();
        fetchDriverExperiences();
        fetchGuarantors();
    }, [id]);

    const fetchDriverDetails = async () => {
        try {
            const response = await fetch(`/api/v1/potential_drivers/${id}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setDriver(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching driver details:', error);
            setLoading(false);
        }
    };

    const fetchAccidentHistories = async () => {
        try {
            const response = await fetch('/api/v1/accident_histories', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            // Filter to only show accidents for this driver
            const driverAccidents = data.filter(ah => ah.potential_driver_id === parseInt(id));
            setAccidentHistories(driverAccidents);
        } catch (error) {
            console.error('Error fetching accident histories:', error);
        }
    };

    const handleDeleteAccident = (e, accidentId) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteItem({ type: 'accident', id: accidentId });
        setShowDeleteConfirm(true);
    };

    const fetchEmploymentHistories = async () => {
        try {
            const response = await fetch('/api/v1/employment_histories', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            // Filter to only show employment for this driver
            const driverEmployment = data.filter(eh => eh.potential_driver_id === parseInt(id));
            setEmploymentHistories(driverEmployment);
        } catch (error) {
            console.error('Error fetching employment histories:', error);
        }
    };

    const handleDeleteEmployment = (e, employmentId) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteItem({ type: 'employment', id: employmentId });
        setShowDeleteConfirm(true);
    };

    const fetchDriverExperiences = async () => {
        try {
            const response = await fetch('/api/v1/driver_experiences', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            // Filter to only show experiences for this driver
            const driverExps = data.filter(de => de.potential_driver_id === parseInt(id));
            setDriverExperiences(driverExps);
        } catch (error) {
            console.error('Error fetching driver experiences:', error);
        }
    };

    const handleDeleteExperience = (e, experienceId) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteItem({ type: 'experience', id: experienceId });
        setShowDeleteConfirm(true);
    };

    const fetchGuarantors = async () => {
        try {
            const response = await fetch(`/api/v1/guarantors?potential_driver_id=${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setGuarantors(data);
        } catch (error) {
            console.error('Error fetching guarantors:', error);
        }
    };

    const handleDeleteGuarantor = (e, guarantorId) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteItem({ type: 'guarantor', id: guarantorId });
        setShowDeleteConfirm(true);
    };

    const executeDelete = async () => {
        if (!deleteItem) return;

        try {
            let url = '';
            switch (deleteItem.type) {
                case 'accident':
                    url = `/api/v1/accident_histories/${deleteItem.id}`;
                    break;
                case 'employment':
                    url = `/api/v1/employment_histories/${deleteItem.id}`;
                    break;
                case 'experience':
                    url = `/api/v1/driver_experiences/${deleteItem.id}`;
                    break;
                case 'guarantor':
                    url = `/api/v1/guarantors/${deleteItem.id}`;
                    break;
            }

            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });

            if (response.ok) {
                switch (deleteItem.type) {
                    case 'accident':
                        setAccidentHistories(accidentHistories.filter(ah => ah.id !== deleteItem.id));
                        break;
                    case 'employment':
                        setEmploymentHistories(employmentHistories.filter(eh => eh.id !== deleteItem.id));
                        break;
                    case 'experience':
                        setDriverExperiences(driverExperiences.filter(de => de.id !== deleteItem.id));
                        break;
                    case 'guarantor':
                        setGuarantors(guarantors.filter(g => g.id !== deleteItem.id));
                        break;
                }
                setShowDeleteConfirm(false);
                setDeleteItem(null);
            } else {
                console.error('Failed to delete item');
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
        },
        header: {
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
        },
        title: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '0.5rem',
        },
        subtitle: {
            fontSize: '1rem',
            color: isDark ? '#9ca3af' : '#6b7280',
        },
        backButton: {
            padding: '0.75rem 1.5rem',
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            marginBottom: '1rem',
            marginLeft: '1rem',
        },
        statusBadge: (status) => ({
            display: 'inline-block',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            background: status === 'APPROVED' ? '#10b981' : status === 'REJECTED' ? '#ef4444' : '#f59e0b',
            color: 'white',
            marginLeft: '1rem',
        }),
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.5rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
        },
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        cardHeader: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
        },
        field: {
            marginBottom: '1rem',
        },
        label: {
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.25rem',
        },
        value: {
            fontSize: '0.875rem',
            color: isDark ? '#d1d5db' : '#374151',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '0.75rem',
            borderBottom: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
        },
        td: {
            padding: '0.75rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
            fontSize: '0.875rem',
        },
        emptyState: {
            textAlign: 'center',
            padding: '2rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            fontStyle: 'italic',
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={{ textAlign: 'center', padding: '4rem' }}>
                    <div style={{ fontSize: '1.25rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        Loading driver details...
                    </div>
                </div>
            </div>
        );
    }

    if (!driver) {
        return (
            <div style={styles.container}>
                <div style={{ textAlign: 'center', padding: '4rem' }}>
                    <div style={{ fontSize: '1.25rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        Driver not found
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <button
                onClick={() => window.location.href = '/potential_drivers'}
                style={styles.backButton}
            >
                ← Back to Potential Drivers
            </button>

            <div style={styles.header}>
                <h1 style={styles.title}>
                    {driver.first_name} {driver.last_name}
                    <span style={styles.statusBadge(driver.status)}>
                        {driver.status || 'PENDING'}
                    </span>
                </h1>
                <p style={styles.subtitle}>Potential Driver Details</p>
            </div>

            {/* Personal Information */}
            <div style={styles.grid}>
                <div style={styles.card}>
                    <h2 style={styles.cardHeader}>👤 Personal Information</h2>
                    <div style={styles.field}>
                        <div style={styles.label}>Full Name</div>
                        <div style={styles.value}>{driver.first_name} {driver.last_name}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Date of Birth</div>
                        <div style={styles.value}>{driver.dob || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>TIN</div>
                        <div style={styles.value}>{driver.tin || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Occupation</div>
                        <div style={styles.value}>{driver.occupation || 'N/A'}</div>
                    </div>
                </div>

                <div style={styles.card}>
                    <h2 style={styles.cardHeader}>🪪 Identification</h2>
                    <div style={styles.field}>
                        <div style={styles.label}>ID Type</div>
                        <div style={styles.value}>{driver.id_type || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>ID Number</div>
                        <div style={styles.value}>{driver.id_number || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>License Type</div>
                        <div style={styles.value}>{driver.liscence_type || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>License Number</div>
                        <div style={styles.value}>{driver.liscence_number || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Contact Information */}
            <div style={styles.grid}>
                <div style={styles.card}>
                    <h2 style={styles.cardHeader}>📞 Contact Information</h2>
                    <div style={styles.field}>
                        <div style={styles.label}>Phone Number</div>
                        <div style={styles.value}>{driver.phone_number || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Email</div>
                        <div style={styles.value}>{driver.email || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Address</div>
                        <div style={styles.value}>{driver.address || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Landmark</div>
                        <div style={styles.value}>{driver.landmark || 'N/A'}</div>
                    </div>
                </div>

                <div style={styles.card}>
                    <h2 style={styles.cardHeader}>👨‍👩‍👧‍👦 Marital & Family</h2>
                    <div style={styles.field}>
                        <div style={styles.label}>Marital Status</div>
                        <div style={styles.value}>{driver.marital_status || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Religion</div>
                        <div style={styles.value}>{driver.religion || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Spouse Name</div>
                        <div style={styles.value}>{driver.name_of_spouse || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Spouse Occupation</div>
                        <div style={styles.value}>{driver.spouse_occupation || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Children / Dependencies</div>
                        <div style={styles.value}>
                            {driver.number_of_children || '0'} children, {driver.number_of_dependencies || '0'} dependencies
                        </div>
                    </div>
                </div>
            </div>

            {/* Emergency Contact */}
            <div style={{ ...styles.card, marginBottom: '1.5rem', marginLeft: '1rem', marginRight: '1rem' }}>
                <h2 style={styles.cardHeader}>🚨 Emergency Contact</h2>
                <div style={styles.grid}>
                    <div style={styles.field}>
                        <div style={styles.label}>Name</div>
                        <div style={styles.value}>{driver.emc_name || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Phone Number</div>
                        <div style={styles.value}>{driver.emc_number || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Address</div>
                        <div style={styles.value}>{driver.emc_address || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Relation</div>
                        <div style={styles.value}>{driver.emc_relation || 'N/A'}</div>
                    </div>
                    <div style={styles.field}>
                        <div style={styles.label}>Occupation</div>
                        <div style={styles.value}>{driver.emc_occupation || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Accident Histories Section */}
            <div style={{ ...styles.card, marginBottom: '1.5rem', marginLeft: '1rem', marginRight: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={styles.cardHeader}>🚗 Accident Histories</h2>
                    <button
                        onClick={() => {
                            setEditingAccident(null);
                            setShowAccidentModal(true);
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        + Add Accident
                    </button>
                </div>
                {accidentHistories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        No accident histories recorded
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Location</th>
                                    <th style={styles.th}>Casualties</th>
                                    <th style={styles.th}>Car State</th>
                                    <th style={styles.th}>Description</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accidentHistories.map((accident) => (
                                    <tr key={accident.id}>
                                        <td style={styles.td}>{accident.date}</td>
                                        <td style={styles.td}>{accident.location}</td>
                                        <td style={styles.td}>{accident.number_of_casualties || 0}</td>
                                        <td style={styles.td}>{accident.car_state || 'N/A'}</td>
                                        <td style={styles.td}>{accident.description || 'N/A'}</td>
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => {
                                                    setEditingAccident(accident);
                                                    setShowAccidentModal(true);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#1e40af' : '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    marginRight: '0.5rem',
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={(e) => handleDeleteAccident(e, accident.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#991b1b' : '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Employment Histories Section */}
            <div style={{ ...styles.card, marginBottom: '1.5rem', marginLeft: '1rem', marginRight: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={styles.cardHeader}>💼 Employment Histories</h2>
                    <button
                        onClick={() => {
                            setEditingEmployment(null);
                            setShowEmploymentModal(true);
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        + Add Employment
                    </button>
                </div>
                {employmentHistories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        No employment histories recorded
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employer</th>
                                    <th style={styles.th}>Address</th>
                                    <th style={styles.th}>Position</th>
                                    <th style={styles.th}>Period Start</th>
                                    <th style={styles.th}>Period End</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employmentHistories.map((employment) => (
                                    <tr key={employment.id}>
                                        <td style={styles.td}>{employment.employer_name}</td>
                                        <td style={styles.td}>{employment.employer_address || 'N/A'}</td>
                                        <td style={styles.td}>{employment.position}</td>
                                        <td style={styles.td}>{employment.period_start || 'N/A'}</td>
                                        <td style={styles.td}>{employment.period_end || 'N/A'}</td>
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => {
                                                    setEditingEmployment(employment);
                                                    setShowEmploymentModal(true);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#1e40af' : '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    marginRight: '0.5rem',
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={(e) => handleDeleteEmployment(e, employment.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#991b1b' : '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Driver Experiences Section */}
            <div style={{ ...styles.card, marginBottom: '1.5rem', marginLeft: '1rem', marginRight: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 style={styles.cardHeader}>🚙 Driver Experiences</h2>
                    <button
                        onClick={() => {
                            setEditingExperience(null);
                            setShowExperienceModal(true);
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        + Add Experience
                    </button>
                </div>
                {driverExperiences.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        No driver experiences recorded
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>License Number</th>
                                    <th style={styles.th}>License Type</th>
                                    <th style={styles.th}>Expiration Date</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {driverExperiences.map((experience) => (
                                    <tr key={experience.id}>
                                        <td style={styles.td}>{experience.country || 'N/A'}</td>
                                        <td style={styles.td}>{experience.liscence_number || 'N/A'}</td>
                                        <td style={styles.td}>{experience.type || 'N/A'}</td>
                                        <td style={styles.td}>{experience.expiration_date || 'N/A'}</td>
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => {
                                                    setEditingExperience(experience);
                                                    setShowExperienceModal(true);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#1e40af' : '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    marginRight: '0.5rem',
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={(e) => handleDeleteExperience(e, experience.id)}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#991b1b' : '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Guarantors */}
            <div style={{ ...styles.card, marginBottom: '1.5rem', marginLeft: '1rem', marginRight: '1rem' }}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.cardTitle}>🤝 Guarantors</h2>
                    <button
                        onClick={() => {
                            setEditingGuarantor(null);
                            setShowGuarantorModal(true);
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <span>+</span> Add Guarantor
                    </button>
                </div>

                {guarantors.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        No guarantors recorded yet.
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Name</th>
                                    <th style={styles.th}>Phone</th>
                                    <th style={styles.th}>Relationship</th>
                                    <th style={styles.th}>Address</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {guarantors.map((guarantor) => (
                                    <tr key={guarantor.id}>
                                        <td style={styles.td}>{guarantor.first_name} {guarantor.last_name}</td>
                                        <td style={styles.td}>{guarantor.phone_number || 'N/A'}</td>
                                        <td style={styles.td}>{guarantor.relationship_to_driver || 'N/A'}</td>
                                        <td style={styles.td}>{guarantor.address || 'N/A'}</td>
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => {
                                                    setEditingGuarantor(guarantor);
                                                    setShowGuarantorModal(true);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#1e40af' : '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    marginRight: '0.5rem',
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setDeleteItem({ type: 'guarantor', id: guarantor.id });
                                                    setShowDeleteConfirm(true);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: isDark ? '#991b1b' : '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0.375rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>



            {/* Accident History Modal */}
            <AccidentHistoryModal
                isOpen={showAccidentModal}
                onClose={() => {
                    setShowAccidentModal(false);
                    setEditingAccident(null);
                }}
                accidentHistory={editingAccident}
                potentialDriverId={driver.id}
                onSave={() => {
                    setShowAccidentModal(false);
                    setEditingAccident(null);
                    fetchAccidentHistories();
                }}
            />

            {/* Employment History Modal */}
            <EmploymentHistoryModal
                isOpen={showEmploymentModal}
                onClose={() => {
                    setShowEmploymentModal(false);
                    setEditingEmployment(null);
                }}
                employmentHistory={editingEmployment}
                potentialDriverId={driver.id}
                onSave={() => {
                    setShowEmploymentModal(false);
                    setEditingEmployment(null);
                    fetchEmploymentHistories();
                }}
            />

            {/* Driver Experience Modal */}
            <DriverExperienceModal
                isOpen={showExperienceModal}
                onClose={() => {
                    setShowExperienceModal(false);
                    setEditingExperience(null);
                }}
                driverExperience={editingExperience}
                potentialDriverId={driver.id}
                onSave={() => {
                    setShowExperienceModal(false);
                    setEditingExperience(null);
                    fetchDriverExperiences();
                }}
            />
            <GuarantorModal
                isOpen={showGuarantorModal}
                onClose={() => setShowGuarantorModal(false)}
                guarantor={editingGuarantor}
                potentialDriverId={id}
                onSave={() => {
                    fetchGuarantors();
                }}
            />
            <ConfirmDeleteModal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteItem(null);
                }}
                onConfirm={executeDelete}
                title={`Delete ${deleteItem?.type === 'accident' ? 'Accident History' : deleteItem?.type === 'employment' ? 'Employment History' : 'Driver Experience'}`}
                message="Are you sure you want to delete this record? This action cannot be undone."
            />
        </div>
    );
};

export default PotentialDriverDetail;
